/**
 * Error severity levels for structured error handling
 */
export enum ErrorSeverity {
  Fatal = 'fatal',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

/**
 * Error categories for classification
 */
export enum ErrorCategory {
  Authentication = 'authentication',
  Authorization = 'authorization',
  Validation = 'validation',
  Network = 'network',
  Database = 'database',
  Payment = 'payment',
  ThirdParty = 'third_party',
  Configuration = 'configuration',
  Internal = 'internal',
  User = 'user',
  System = 'system',
}

/**
 * Standard error codes for consistent error handling
 */
export enum ErrorCode {
  // Authentication errors (1000-1099)
  AUTH_INVALID_TOKEN = 'AUTH_INVALID_TOKEN',
  AUTH_EXPIRED_TOKEN = 'AUTH_EXPIRED_TOKEN',
  AUTH_MISSING_TOKEN = 'AUTH_MISSING_TOKEN',
  AUTH_INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS',
  AUTH_USER_NOT_FOUND = 'AUTH_USER_NOT_FOUND',
  AUTH_USER_SUSPENDED = 'AUTH_USER_SUSPENDED',
  AUTH_INSUFFICIENT_PERMISSIONS = 'AUTH_INSUFFICIENT_PERMISSIONS',

  // Validation errors (1100-1199)
  VALIDATION_REQUIRED_FIELD = 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT',
  VALIDATION_OUT_OF_RANGE = 'VALIDATION_OUT_OF_RANGE',
  VALIDATION_INVALID_TYPE = 'VALIDATION_INVALID_TYPE',
  VALIDATION_CONSTRAINT_VIOLATION = 'VALIDATION_CONSTRAINT_VIOLATION',

  // Network errors (1200-1299)
  NETWORK_REQUEST_FAILED = 'NETWORK_REQUEST_FAILED',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_CONNECTION_REFUSED = 'NETWORK_CONNECTION_REFUSED',
  NETWORK_DNS_ERROR = 'NETWORK_DNS_ERROR',
  NETWORK_RATE_LIMITED = 'NETWORK_RATE_LIMITED',

  // Database errors (1300-1399)
  DATABASE_CONNECTION_FAILED = 'DATABASE_CONNECTION_FAILED',
  DATABASE_QUERY_FAILED = 'DATABASE_QUERY_FAILED',
  DATABASE_CONSTRAINT_VIOLATION = 'DATABASE_CONSTRAINT_VIOLATION',
  DATABASE_DUPLICATE_KEY = 'DATABASE_DUPLICATE_KEY',
  DATABASE_RECORD_NOT_FOUND = 'DATABASE_RECORD_NOT_FOUND',
  DATABASE_TRANSACTION_FAILED = 'DATABASE_TRANSACTION_FAILED',

  // Payment errors (1400-1499)
  PAYMENT_INSUFFICIENT_FUNDS = 'PAYMENT_INSUFFICIENT_FUNDS',
  PAYMENT_INVALID_CARD = 'PAYMENT_INVALID_CARD',
  PAYMENT_PROCESSING_FAILED = 'PAYMENT_PROCESSING_FAILED',
  PAYMENT_SUBSCRIPTION_EXPIRED = 'PAYMENT_SUBSCRIPTION_EXPIRED',
  PAYMENT_PLAN_NOT_FOUND = 'PAYMENT_PLAN_NOT_FOUND',

  // Third-party service errors (1500-1599)
  THIRD_PARTY_SERVICE_UNAVAILABLE = 'THIRD_PARTY_SERVICE_UNAVAILABLE',
  THIRD_PARTY_API_ERROR = 'THIRD_PARTY_API_ERROR',
  THIRD_PARTY_RATE_LIMITED = 'THIRD_PARTY_RATE_LIMITED',
  THIRD_PARTY_AUTHENTICATION_FAILED = 'THIRD_PARTY_AUTHENTICATION_FAILED',

  // Configuration errors (1600-1699)
  CONFIG_MISSING_REQUIRED = 'CONFIG_MISSING_REQUIRED',
  CONFIG_INVALID_VALUE = 'CONFIG_INVALID_VALUE',
  CONFIG_ENVIRONMENT_MISMATCH = 'CONFIG_ENVIRONMENT_MISMATCH',

  // Internal errors (1700-1799)
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  INTERNAL_SERVICE_UNAVAILABLE = 'INTERNAL_SERVICE_UNAVAILABLE',
  INTERNAL_UNEXPECTED_ERROR = 'INTERNAL_UNEXPECTED_ERROR',

  // User/Business logic errors (1800-1899)
  USER_SUBSCRIPTION_REQUIRED = 'USER_SUBSCRIPTION_REQUIRED',
  USER_LIMIT_EXCEEDED = 'USER_LIMIT_EXCEEDED',
  USER_FEATURE_NOT_AVAILABLE = 'USER_FEATURE_NOT_AVAILABLE',
  USER_ALREADY_EXISTS = 'USER_ALREADY_EXISTS',

  // System errors (1900-1999)
  SYSTEM_MAINTENANCE = 'SYSTEM_MAINTENANCE',
  SYSTEM_OVERLOADED = 'SYSTEM_OVERLOADED',
  SYSTEM_RESOURCE_EXHAUSTED = 'SYSTEM_RESOURCE_EXHAUSTED',
}

/**
 * Error context for additional debugging information
 */
export interface ErrorContext {
  /** Request ID for tracing */
  requestId?: string;
  /** User ID associated with the error */
  userId?: string;
  /** User email (should be omitted for PII safety in production) */
  userEmail?: string;
  /** HTTP method */
  method?: string;
  /** Request URL */
  url?: string;
  /** Route path */
  route?: string;
  /** HTTP status code */
  statusCode?: number;
  /** Stack trace */
  stack?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  /** Timestamp of the error */
  timestamp?: Date;
  /** Service/component that generated the error */
  service?: string;
  /** Operation that failed */
  operation?: string;
}

/**
 * Error response format for API responses
 */
export interface ErrorResponse {
  success: false;
  error: {
    code: ErrorCode;
    message: string;
    category: ErrorCategory;
    severity: ErrorSeverity;
    context?: Partial<ErrorContext>;
  };
}

/**
 * Base error properties
 */
export interface BaseErrorOptions {
  code: ErrorCode;
  message: string;
  category: ErrorCategory;
  severity?: ErrorSeverity;
  context?: ErrorContext;
  cause?: Error;
}
